import axios from 'axios'
import { acceptHMRUpdate, defineStore } from 'pinia'
import type { FullMobilePhoto, LightMobilePhoto, SupabaseSession, SupabaseUser } from './types'

// const API_URL = import.meta.env.DEV ? 'http://localhost:8040' : 'https://api.valveilla.net'
const API_URL = 'https://api.valveilla.net'
const api = axios.create({
  baseURL: API_URL,
})

const versionString =
  import.meta.env.MODE === 'development' ? import.meta.env.VITE_APP_VERSION + '-dev' : import.meta.env.VITE_APP_VERSION

export const useStore = defineStore('main', {
  state: () => ({
    debug: import.meta.env.DEV,
    version: versionString,
    apiVersion: '',
    isInitialized: false,
    sidebarVisible: false,
    user: null as SupabaseUser | null,
    session: null as SupabaseSession | null,
    isLoading: false,
    formError: '',
    pageViews: {
      count: 0,
      isCounted: false,
      isVip: false,
    },
    lightPhotos: [] as LightMobilePhoto[],
    fullPhotos: [] as FullMobilePhoto[],
    selectedLightPhotoId: null as string | null,
  }),

  actions: {
    initApp(apiVersion: string = '') {
      if (this.isInitialized) {
        if (this.debug) {
          console.log('App already initialized')
        }
        return
      } else {
        if (this.debug) {
          console.log('Initing store')
        }
      }
      this.apiVersion = apiVersion
      this.fetchLightPhotos()
      this.isLoading = true

      const localUser = localStorage.getItem('user')
      const localSession = localStorage.getItem('session')
      if (localUser) {
        this.user = JSON.parse(localUser)
        // console.log('User found in local storage:', this.user)
      }
      if (localSession) {
        this.session = JSON.parse(localSession)
        // console.log('Session found in local storage:', this.session)
      }

      this.pageViewCount()
      this.isInitialized = true
      this.isLoading = false
    },

    async fetchLightPhotos() {
      if (this.isLoading) {
        return
      }

      this.isLoading = true
      try {
        const response = await api.get<LightMobilePhoto[]>('/photos/')
        this.lightPhotos = response.data.sort((a, b) => b.timestamp - a.timestamp)
      } catch (err) {
        console.error('Failed to fetch light photos:', err)
      } finally {
        this.isLoading = false
      }
    },

    async getOrFetchFullPhoto(photoId: string) {
      if (this.isLoading) {
        return
      }

      const existingPhoto = this.fullPhotos.find(photo => photo.uid === photoId)
      if (existingPhoto) {
        return existingPhoto
      }

      this.isLoading = true
      try {
        const response = await api.get<FullMobilePhoto>(`/photos/${photoId}/`)
        this.fullPhotos.push(response.data)
        return response.data
      } catch (err) {
        console.error('Failed to fetch full photo:', err)
      } finally {
        this.isLoading = false
      }
    },

    toggleSidebar() {
      this.sidebarVisible = !this.sidebarVisible
    },

    async logoutUser() {
      if (this.isLoading) {
        return
      }
      if (!this.session) {
        this.formError = 'Not authenticated'
        return
      }

      this.formError = ''
      this.isLoading = true

      try {
        const response = await api.post(
          '/auth/logout/',
          {},
          {
            headers: {
              Authorization: `Bearer ${this.session.access_token}`,
            },
          }
        )

        localStorage.removeItem('user')
        localStorage.removeItem('session')
        window.location.reload()
      } catch (err) {
        if (axios.isAxiosError(err)) {
          this.formError = err.response?.data?.detail || 'Logout failed'
        } else {
          this.formError = 'An unexpected error occurred'
        }
      } finally {
        this.isLoading = false
      }
    },

    async loginUser(email: string, password: string) {
      if (this.isLoading) {
        return
      }

      this.formError = ''
      this.isLoading = true

      try {
        const response = await api.post<{ user: SupabaseUser; session: SupabaseSession }>('/auth/login/', {
          email,
          password,
        })

        this.user = response.data.user
        this.session = response.data.session

        localStorage.setItem('user', JSON.stringify(response.data.user))
        localStorage.setItem('session', JSON.stringify(response.data.session))
      } catch (err) {
        if (axios.isAxiosError(err)) {
          this.formError = err.response?.data?.detail || 'Kirjautuminen epäonnistui'
        } else {
          this.formError = 'Ups. Odottamaton virhe'
        }
      } finally {
        this.isLoading = false
      }
    },

    preloadImage(url: string): Promise<void> {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve()
        img.onerror = reject
        img.src = url
      })
    },

    pageViewCount() {
      const COUNTING_DELAY_SECONDS = 30
      const VIP_TRESHOLD = 5
      const STORAGE_KEY = 'o'
      const VIP_KEY = 'v'

      this.pageViews.count = Number(localStorage.getItem(STORAGE_KEY) ?? 0)
      this.pageViews.isVip = localStorage.getItem(VIP_KEY) === 'true'

      const incrementCount = () => {
        if (this.pageViews.isCounted) {
          return
        }
        if (this.debug) {
          console.log('Incrementing page view count')
        }

        this.pageViews.count++
        localStorage.setItem(STORAGE_KEY, String(this.pageViews.count))
        this.pageViews.isCounted = true

        if (this.pageViews.count >= VIP_TRESHOLD && !this.pageViews.isVip) {
          this.pageViews.isVip = true
          localStorage.setItem(VIP_KEY, 'true')
        }
      }

      setTimeout(incrementCount, COUNTING_DELAY_SECONDS * 1000)
    },
  },

  getters: {
    isReady: state => {
      return !state.isInitialized && !state.isLoading
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useStore, import.meta.hot))
}
